// 人件費率 設定ローダー（2026-08-27 一本化。旧 payroll.js の後継）。
// settings/labor ドキュメント {estimatePct} と drivers マスタ（rates:[{from,pct}] 適用開始日つき）を読み、
// operations.js の setLabor() へ流し込む。乗務員別の率は常に収支へ適用（旧「収支に反映」トグルは廃止）。
// estimatePct＝「試算用」率。受注試算(quoteOwnTrip)に使い、個別設定が無い乗務員のフォールバック。
import { COLLECTIONS, listDocs, getOne, upsert, invalidateCache } from './collections.js'
import { setLabor } from './analytics/operations.js'
import { loadDemoDrivers } from './driverMasters.js'
import { resolveEstimatePct, normPct } from './laborRates.core.js'
import { normLaborTime } from './laborQuote.core.js'
import { supabaseReady } from '../supabase.js'

// 所要時間ベース試算（2026-08-28）の新フィールド。生値（null＝未設定）を UI へ渡し、
// 収支エンジン側へは normLaborTime で既定値に正規化して渡す。
const TIME_FIELDS = ['hourlyMedium', 'hourlyLarge', 'driveFactor', 'stopBufferMin']
const pickTimeFields = (s) => Object.fromEntries(TIME_FIELDS.map((k) => [k, s && s[k] != null ? s[k] : null]))

const norm = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')

let _cache = null, _at = 0
const TTL = 60 * 1000

export function invalidateLabor() { _cache = null; _at = 0 }

// 設定ドキュメントの読取（無ければ既定：率null＝LABOR_RATE 35%）。
// 移行読み: settings/labor 未作成の間だけ、旧 settings/payroll.laborRatePct（#4 の旧既定歩合率）を
// 初期値として引き継ぐ。これが旧2系統への唯一の参照。保存は settings/labor のみ＝旧docへは書かない。
export async function loadLaborSettings() {
  if (!supabaseReady) return { estimatePct: null, ...pickTimeFields(null) }
  try {
    const s = await getOne(COLLECTIONS.settings, 'labor')
    const legacy = s ? null : await getOne(COLLECTIONS.settings, 'payroll').catch(() => null)
    return { estimatePct: resolveEstimatePct(s, legacy), ...pickTimeFields(s) }
  } catch { return { estimatePct: null, ...pickTimeFields(null) } }
}

// 設定の保存（UIから）。保存後は必ず refreshLabor(true) で反映。
export async function saveLaborSettings(patch) {
  if (!supabaseReady) return
  const cur = await loadLaborSettings()
  const next = { ...cur, ...patch, updatedAt: new Date().toISOString() }
  await upsert(COLLECTIONS.settings, 'labor', next)
  invalidateLabor()
}

// drivers マスタ＋設定 → operations.js へ流し込む。TTLキャッシュ（force で即再取得）。
export async function refreshLabor(force) {
  // デモ（Supabase未設定）: 乗務員マスタはブラウザローカルの保管庫（driverMasters.js）が代役。
  // 空なら従来どおり drivers=null＝全員が試算用率のフォールバック＝数字は従来と同じ。
  if (!supabaseReady) {
    const demo = loadDemoDrivers()
    const cfg = { rate: null, drivers: Object.keys(demo).length ? demo : null }
    setLabor(cfg)
    return null
  }
  if (!force && _cache && (Date.now() - _at) < TTL) { setLabor(_cache); return _cache }
  try {
    const s = await loadLaborSettings()
    const drv = await listDocs(COLLECTIONS.drivers).catch(() => [])
    const drivers = {}
    for (const m of (drv || [])) { const id = norm(m.code || m.id || ''); if (id) drivers[id] = m }
    const pct = normPct(s.estimatePct)
    // time＝所要時間ベース試算の時給・係数（未設定・不正値は normLaborTime が既定へ倒す）
    const cfg = { rate: pct != null ? pct / 100 : null, drivers, time: normLaborTime(s) }
    setLabor(cfg)
    _cache = cfg; _at = Date.now()
    return cfg
  } catch {
    // 取得失敗で乗務員別の率を捨てると、収支の人件費が全員フォールバック率に化ける。
    // 一度読めている設定があるならそれを保つ（初回失敗時だけ既定へ倒す）。
    if (_cache) { setLabor(_cache); return _cache }
    setLabor({ rate: null, drivers: null })
    return null
  }
}

export { invalidateCache }
